import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import "./Registration.css";

const LOCAL_API_BASE_URL = "http://localhost:5000";
const RENDER_API_BASE_URL = "https://wecareehs-backend.onrender.com";
const API_BASE_URL =
  process.env.REACT_APP_USE_RENDER === "true" ? RENDER_API_BASE_URL : LOCAL_API_BASE_URL;

// ── Password Strength Logic ───────────────────────────────────────────────────
function getPasswordStrength(password) {
  if (!password) return { score: 0, label: "", color: "" };

  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    longEnough: password.length >= 12,
  };

  const score = Object.values(checks).filter(Boolean).length;

  if (score <= 1) return { score: 1, label: "Weak", color: "#e74c3c", checks };
  if (score === 2) return { score: 2, label: "Fair", color: "#f39c12", checks };
  if (score === 3) return { score: 3, label: "Good", color: "#f1c40f", checks };
  if (score === 4) return { score: 4, label: "Strong", color: "#2ecc71", checks };
  return { score: 5, label: "Very Strong", color: "#00b894", checks };
}

function PasswordStrengthMeter({ password }) {
  const strength = useMemo(() => getPasswordStrength(password), [password]);

  if (!password) return null;

  const requirements = [
    { label: "At least 8 characters", met: strength.checks?.length },
    { label: "One uppercase letter", met: strength.checks?.uppercase },
    { label: "One number", met: strength.checks?.number },
    { label: "One special character (!@#$...)", met: strength.checks?.special },
  ];

  return (
    <div className="password-strength-container">
      <div className="strength-bar-track">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="strength-bar-segment"
            style={{
              background: i <= strength.score ? strength.color : "rgba(255,255,255,0.08)",
              transition: "background 0.3s ease",
            }}
          />
        ))}
      </div>
      <span className="strength-label" style={{ color: strength.color }}>
        {strength.label}
      </span>
      <ul className="strength-requirements">
        {requirements.map((req) => (
          <li key={req.label} className={req.met ? "req-met" : "req-unmet"}>
            {req.met ? (
              <FaCheckCircle style={{ color: "#2ecc71" }} />
            ) : (
              <FaTimesCircle style={{ color: "#555" }} />
            )}
            {req.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Registration Form ─────────────────────────────────────────────────────────
function Registration() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    company: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const passwordStrength = useMemo(
    () => getPasswordStrength(formData.password),
    [formData.password]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // Enforce strength requirement before sending to server
    if (passwordStrength.score < 3) {
      setError("Password is too weak. Please meet all requirements.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          phone: formData.phone.trim(),
          company: formData.company.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        navigate("/login", { state: { message: "Account created! Please sign in." } });
      } else {
        setError(data.error || data.message || "Registration failed. Please try again.");
      }
    } catch (err) {
      setError("Registration failed. Please check your connection and try again.");
    }

    setLoading(false);
  };

  return (
    <div className="split-login-container">
      {/* Left Visual Panel */}
      <div className="visual-panel register-visual">
        <div className="visual-content">
          <h1 className="visual-heading">
            Join the
            <br />
            Evolution.
          </h1>
          <p className="visual-subtext">Create an account and start your journey.</p>
        </div>
        <div className="abstract-shape register-shape"></div>
        <div className="abstract-shape-2"></div>
      </div>

      {/* Right Form Panel */}
      <div className="form-panel">
        <div className="form-content register-content">
          <div className="brand-logo-area">
            <div className="logo-dot"></div>
          </div>

          <div className="login-header">
            <h2>Create Account</h2>
            <p>Fill in your details to get started.</p>
          </div>

          <form onSubmit={handleSubmit} className="split-form" autoComplete="on">
            <div className="form-row-split">
              <div className="minimal-input-group">
                <label htmlFor="reg-firstName">First Name</label>
                <input
                  id="reg-firstName"
                  type="text"
                  name="firstName"
                  className="minimal-input"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="John"
                  autoComplete="given-name"
                  required
                />
              </div>
              <div className="minimal-input-group">
                <label htmlFor="reg-lastName">Last Name</label>
                <input
                  id="reg-lastName"
                  type="text"
                  name="lastName"
                  className="minimal-input"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Doe"
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>

            <div className="minimal-input-group">
              <label htmlFor="reg-email">Email Address</label>
              <input
                id="reg-email"
                type="email"
                name="email"
                className="minimal-input"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john.doe@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="form-row-split">
              <div className="minimal-input-group">
                <label htmlFor="reg-password">Password</label>
                <input
                  id="reg-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="minimal-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create password"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="minimal-input-group">
                <label htmlFor="reg-confirmPassword">Confirm</label>
                <input
                  id="reg-confirmPassword"
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  className="minimal-input"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm password"
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            {/* Password Strength Meter */}
            <PasswordStrengthMeter password={formData.password} />

            <div className="minimal-input-group">
              <label htmlFor="reg-phone">Phone Number</label>
              <input
                id="reg-phone"
                type="tel"
                name="phone"
                className="minimal-input"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+91 98765 43210"
                autoComplete="tel"
                required
              />
            </div>

            <div className="minimal-input-group">
              <label htmlFor="reg-company">Company (Optional)</label>
              <input
                id="reg-company"
                type="text"
                name="company"
                className="minimal-input"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Company Name"
                autoComplete="organization"
              />
            </div>

            {error && <div className="status-message error">{error}</div>}

            <button
              type="submit"
              className="action-btn"
              disabled={loading || passwordStrength.score < 3}
            >
              {loading ? "Creating Account..." : "Register Now"} <FaArrowRight />
            </button>
          </form>

          <div className="panel-footer">
            <p>
              Already a member?{" "}
              <a href="/login" className="highlight-link">
                Sign In
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Registration;
